import React from "react";
import { TripLodging } from "@/types";
import { formatImageUrl } from "@/lib/general";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Button } from "@/components/ui/button";
import { HomeIcon } from "@heroicons/react/24/outline";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useViewStore from "@/stores/tripBuilder/useView";

dayjs.extend(timezone);
dayjs.extend(utc);

interface LodgingItemProps {
  lodging: TripLodging;
  locationTimeZone: string;
  locationName: string;
  selectedDay: string;
  showLocationName?: boolean;
}

const LodgingItem: React.FC<LodgingItemProps> = ({
  lodging,
  locationTimeZone,
  locationName,
  selectedDay,
  showLocationName = true,
}) => {
  const { removeLodging, handleModalOpenTripLodging } = useAccommodationStore();
  const { setActiveTab } = useViewStore();
  const checkInTime = dayjs(lodging.policies?.checkIn || lodging.start_time).tz(
    locationTimeZone
  );
  const checkOutTime = dayjs(lodging.policies?.checkOut).tz(locationTimeZone);

  const getTimeText = () => {
    const today = dayjs(selectedDay).tz(locationTimeZone).startOf("day");
    const startDate = checkInTime.startOf("day");
    const endDate = checkOutTime?.startOf("day");
    const lodgingType = lodging.type === "home" ? "home stay" : lodging.type;

    if (today.isSame(startDate, "day")) {
      return `Check in at ${checkInTime.format("h:mm A")}`;
    } else if (endDate && today.isSame(endDate, "day")) {
      return `Check out by ${checkOutTime.format("h:mm A")}`;
    } else {
      return `Your ${lodgingType} in ${locationName}`;
    }
  };

  return (
    <div className="w-full">
      <div className="bg-neutral-100/70 rounded-xl py-3 px-4 w-full">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center space-x-4">
            <HomeIcon className="w-5 h-5 text-neutral-600 2xl:w-5 2xl:h-5" />
            <div className="flex flex-col items-start">
              <p className="text-sm text-neutral-800 font-medium mb-0.5 2xl:text-sm">
                <span
                  className={`text-neutral-800 truncate inline-block align-bottom ${
                    showLocationName ? "max-w-[250px]" : "max-w-[400px] "
                  }`}
                >
                  {lodging.name}
                </span>
                {showLocationName && (
                  <span className="text-sm text-neutral-500 font-light ml-1.5 2xl:text-sm truncate max-w-[100px] inline-block align-bottom">
                    {locationName}
                  </span>
                )}
              </p>
              <p className="text-xs text-neutral-500 font-light 2xl:text-xs">
                {getTimeText()}
              </p>
            </div>
          </div>

          <div className="flex flex-row items-center space-x-2 2xl:space-x-3">
            {lodging.mainImageURL && (
              <img
                src={formatImageUrl(lodging.mainImageURL, "low")}
                alt={lodging.name}
                className="w-8 h-8 rounded-md object-cover 2xl:w-10 2xl:h-10"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LodgingItem;
